import { useState } from 'react'
import { useRouter } from 'next/router'
import { Modal, Button, Alert } from 'antd'
import { useTranslation } from 'next-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { login, clearError } from '@/features/auth/apis/authSlice'
import { errorHandling, openNotification } from '@/utils/utils'
import { getPostAuthRedirectPath } from '@/utils/authRedirect'

import ResetPassword from './ResetPassword'
import Close from '@/components/svgs/CloseModal'
import Email from '@/components/svgs/auth/Email'
import InputAffix from '@/components/shared-components/form/InputAffix'
import PasswordInput from '@/components/shared-components/form/PasswordInput'
import SocialButtons from '@/components/shared-components/SocialButtons'
import Register from './Register'

const Login = ({ open, setOpen, onDismiss }) => {
  const { t } = useTranslation('HomeLayout')
  const router = useRouter()
  const dispatch = useDispatch()

  const { loading, errors, errorMsg } = useSelector((state) => state.auth)

  const [registerOpen, setRegisterOpen] = useState(false)
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false)
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
  })
  const [localErrors, setLocalErrors] = useState({})
  const requiredFieldMessage = 'This field is required'
  const invalidEmailMessage = 'Please enter a valid email address'

  const mergedErrors = {
    ...errors,
    ...localErrors,
  }

  const handleDismiss = () => {
    localStorage.removeItem('SMARTCAF_SERVICE_ID')
    setLocalErrors({})
    setOpen(false)
    dispatch(clearError())
    onDismiss?.()
  }

  const validateForm = () => {
    const nextErrors = {}

    if (!formValues.email.trim()) {
      nextErrors.email = requiredFieldMessage
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim())) {
      nextErrors.email = invalidEmailMessage
    }

    if (!formValues.password.trim()) {
      nextErrors.password = requiredFieldMessage
    }

    setLocalErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  // SUBMITING FORM
  const handleSubmit = (event) => {
    event?.preventDefault()

    if (!validateForm()) {
      return
    }

    dispatch(login(formValues))
      .unwrap()
      .then(() => {
        setOpen(false)
        const nextRedirectPath = getPostAuthRedirectPath(router.query['redirection_link'])

        if (localStorage.getItem('redirection_link')) {
          localStorage.removeItem('redirection_link')
        }

        if (nextRedirectPath) {
          router.push(nextRedirectPath)
        } else if (localStorage.getItem('SMARTCAF_conversation_token')) {
          router.push('/dashboard/tickets')
        } else if (localStorage.getItem('SMARTCAF_SERVICE_ID')) {
          router.push(
            `/dashboard/servizi/${localStorage.getItem(
              'SMARTCAF_SERVICE_ID'
            )}/request`
          )
          localStorage.removeItem('SMARTCAF_SERVICE_ID')
        } else {
          router.push('/dashboard/servizi')
        }
      })
      .catch((error) => {
        const message = errorHandling(error)
        openNotification('error', message)
      })
  }

  return (
    <>
      <Modal
        className='!w-[30rem]'
        title={null}
        open={open}
        onCancel={handleDismiss}
        footer={null}
        closable={false}
        centered
      >
        <div className='text-center mb-32 mt-16'>
          <h3 className='text-xl font-semibold mb-8'>
            {t('Accedi al tuo account')}
          </h3>
        </div>

        <SocialButtons />

        <form className='form-type_5' onSubmit={handleSubmit}>
          <InputAffix
            label={`${t('form.email.label')}*`}
            placeholder={t('form.email.ph')}
            name='email'
            autoComplete='username'
            value={formValues.email}
            onChange={(e) => {
              setFormValues({ ...formValues, email: e.target.value })
              setLocalErrors((prev) => ({ ...prev, email: null }))
            }}
            error={mergedErrors?.email}
            icon={<Email />}
            style='mb-16'
          />

          <PasswordInput
            label={`${t('form.password.label')}*`}
            placeholder={t('form.password.ph')}
            name='password'
            autoComplete='current-password'
            value={formValues.password}
            onChange={(e) => {
              setFormValues({ ...formValues, password: e.target.value })
              setLocalErrors((prev) => ({ ...prev, password: null }))
            }}
            error={mergedErrors?.password}
            style='mb-8'
          />

          <span
            className='inline-block text-green text-sm font-medium cursor-pointer mb-32 flex justify-end'
            onClick={() => {
              localStorage.removeItem('SMARTCAF_SERVICE_ID')
              setLocalErrors({})
              setResetPasswordOpen(true)
              setOpen(false)
              dispatch(clearError())
            }}
          >
            {t('Forgot Password ?')}
          </span>
 
          {errorMsg && !errors && (
            <Alert className='mb-16' message={`${errorMsg}`} type='error' />
          )}

          <Button
            className='block btn-green text-base font-semibold tracking-wide rounded-lg py-12 h-auto mb-20 w-full'
            loading={loading}
            htmlType='submit'
          >
            {t('login.submit')}
          </Button>
        </form>

        <p className='text-[#AAA] text-sm text-center mt-16'>
          {t('login.footer')}{' '}
          <span
            className='text-green font-semibold cursor-pointer'
            onClick={() => {
              setLocalErrors({})
              setOpen(false)
              setRegisterOpen(true)
            }}
          >
            {t('login.footer.link')}
          </span>
        </p>

        <Close
          style='absolute top-28 right-36 cursor-pointer'
          onClick={handleDismiss}
        />
      </Modal>

      <Register open={registerOpen} setOpen={setRegisterOpen} />
      <ResetPassword open={resetPasswordOpen} setOpen={setResetPasswordOpen} />
    </>
  )
}

export default Login
